<template>
  <div class="container mx-auto px-4 py-6">
    <!-- Labour Section Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3">
        <div class="flex items-center justify-between">
          <h2 class="font-semibold text-white text-lg">
            {{ $t('Mobile.MobileLabourDashboard.title') }}
          </h2>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-white">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
</svg>
          </div>
        </div>
      </div>

      <div class="p-4">
        <!-- Labour Records -->
        <div class="space-y-2">
          <LabourRecordRow
            v-for="labour in labours"
            :key="labour.id"
            :labour="labour"
            :project="project"
            :selected-date="selectedDate"
            :data-test="`labour-${labour.id}-records`"
            :project-unarchived="projectsStore.project?.unarchived"
          />
        </div>

        <!-- Loading State -->
        <AppSpinner
          v-if="fetchDailyLaboursState === States.LOADING"
          class="flex w-full justify-center p-4"
        />

        <!-- Empty State -->
        <div
          v-else-if="labours.length === 0 && fetchDailyLaboursState === States.COMPLETE"
          class="text-center py-8"
          data-test="no-labours"
        >
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-8 h-8 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
</svg>
          </div>

          <p class="text-gray-500 font-medium">
            {{ $t('Mobile.MobileLabourDashboard.noLaboursFound', { date: new Date(selectingDateInput).toLocaleDateString() }) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Date Navigation Footer -->
    <Teleport
      v-if="firstLoad"
      to="#footer"
    >
      <div class="w-full bg-white border-t border-gray-200 p-4">
        <div class="max-w-md mx-auto flex items-center space-x-4 justify-center">
          <button
            type="button"
            class="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
            data-test="prev-day"
            @click="prevDate"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
          </button>

          <div class="mx-auto">
            <AppInput
              v-model="selectingDateInput"
              class="form-input"
              type="date"
              name="selected_date"
              data-test="select-day-input"
              @keyup.enter="setDay"
              @change="screenSize < ScreenSize.MD && setDay()"
            />
          </div>

          <button
            type="button"
            class="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
            data-test="next-day"
            @click="nextDate"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import LabourRecordRow from '@/components/Mobile/MobileLabourDashboard/LabourRecordRow.vue';
import useDayQueryParam from '@/composables/useDayQueryParam';
import useScreenSize, { ScreenSize } from '@/composables/useScreenSize';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateAsYMD } from '@/helpers';
import { useLaboursStore } from '@/store/labours';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const queryParams = useRoute().query.day as string;
const selectingDateInput = ref(dateAsYMD(new Date()));
const selectedDate = useDayQueryParam();

if(queryParams) {
  const queryDate = new Date(queryParams);

  if(!isNaN(queryDate.getTime())) {
    selectingDateInput.value = dateAsYMD(queryDate);
  }
}

const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();

wbsCodesStore.index(props.projectId);

const project = computed(() => {
  return projectsStore.models.find(parseInt(props.projectId));
});

const laboursStore = useLaboursStore();
const labourIds = ref([]);

const labours = computed(() => {
  return laboursStore.models.query().with('records', (query) => {
    query.where('day', selectingDateInput.value).with('wbsCode');
  }).whereId(labourIds.value).get();
});

const router = useRouter();

const {
  request: fetchDailyLabours,
  state: fetchDailyLaboursState,
  firstLoad,
} = useStoreApiAction(laboursStore.fetchDailyLabours);

watch(selectingDateInput, (newValue) => {
  if(!newValue) {
    selectingDateInput.value = dateAsYMD(new Date());
    selectedDate.value = new Date();
  } else {
    selectedDate.value = new Date(newValue);
    router.replace({ query: { day: `${newValue}` } });
    labourIds.value = [];

    fetchDailyLabours(props.projectId, { day: dateAsYMD(new Date(newValue)) }).then((dailyLabours) => {
      dailyLabours.forEach((labour) => {
        labourIds.value.push(labour.id);
      });
    });
  }
}, { immediate: true });

const nextDate = () => {
  const selected = new Date(selectingDateInput.value);

  selected.setDate(selected.getDate() + 1);
  selectingDateInput.value = dateAsYMD(selected);
};

const prevDate = () => {
  const selected = new Date(selectingDateInput.value);

  selected.setDate(selected.getDate() - 1);
  selectingDateInput.value = dateAsYMD(selected);
};

const setDay = () => {
  if(!isNaN(new Date(selectingDateInput.value).getTime())) {
    selectedDate.value = new Date(selectingDateInput.value);
  }
};

// const openDateSelector = () => {
//   selectingDate.value = true;

//   nextTick(() => {
//     selectDateInput.value?.focusInput();
//   });
// };

const { screenSize } = useScreenSize();
const navStore = useNavStore();

navStore.backRoute = { name: 'MobileProjectPage' };
</script>

<style scoped></style>
